import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { SignOut, Eye, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ContentDisplay } from "@/components/ContentDisplay";
import { MultiSectionDisplay } from "@/components/MultiSectionDisplay";
import ReactMarkdown from "react-markdown";

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-[var(--bg-hover)] text-[var(--text-muted)] border-[var(--border)]" },
  in_review: { label: "In Review", color: "bg-amber-500/10 text-amber-400 border-amber-500/30" },
  approved: { label: "Approved", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
  published: { label: "Published", color: "bg-blue-500/10 text-blue-400 border-blue-500/30" },
};

function isMultiSectionDraft(draft, contentType) {
  if (!draft) return false;
  const multiTypes = ["newsletter issue", "lead magnet", "content sequence"];
  if (multiTypes.some((t) => (contentType || "").toLowerCase().includes(t))) return true;
  return (draft.match(/^###\s/gm) || []).length >= 2;
}

function parseDraftToSections(draft) {
  const sectionRegex = /^###\s+(.+)$/gm;
  const matches = [...draft.matchAll(sectionRegex)];
  if (matches.length === 0) return { sections: { Content: draft }, section_names: ["Content"] };
  const section_names = [];
  const sections = {};
  matches.forEach((match, i) => {
    const name = match[1].trim();
    const start = match.index + match[0].length;
    const end = i < matches.length - 1 ? matches[i + 1].index : draft.length;
    section_names.push(name);
    sections[name] = draft.slice(start, end).replace(/^[\s-]+/, "").trim();
  });
  return { sections, section_names };
}

export default function ClientDashboard() {
  const { api, user, logout } = useAuth();
  const [history, setHistory] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [viewItem, setViewItem] = useState(null);
  const [brandProfile, setBrandProfile] = useState(null);
  const [activeTab, setActiveTab] = useState("content");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [histResp, brandResp] = await Promise.all([
          api.get(`/history?page=${page}&limit=15`),
          api.get("/business/brand-profile").catch(() => ({ data: null })),
        ]);
        setHistory(histResp.data.items || []);
        setPages(histResp.data.pages || 1);
        setBrandProfile(brandResp.data);
      } catch {
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api, page]);

  const handleStatusChange = async (item, newStatus) => {
    setHistory((prev) =>
      prev.map((i) => (i.history_id === item.history_id ? { ...i, status: newStatus } : i))
    );
    try {
      await api.patch(`/history/${item.history_id}/status`, { status: newStatus });
    } catch (err) {
      setHistory((prev) =>
        prev.map((i) => (i.history_id === item.history_id ? { ...i, status: item.status } : i))
      );
      toast.error(err.response?.data?.detail || "Failed to update status");
    }
  };

  const clientStatusOptions = ["in_review", "approved"];

  return (
    <div className="min-h-screen bg-[var(--bg-base)]" data-testid="client-dashboard">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[var(--bg-card)]/95 backdrop-blur-xl border-b border-[var(--border)]">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-[var(--text-primary)]">
              {user?.active_business_name || "Client Portal"}
            </span>
            <span className="px-2 py-0.5 rounded text-[10px] bg-blue-500/10 text-blue-400 border border-blue-500/30 font-['JetBrains_Mono']">CLIENT</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[var(--text-muted)]">{user?.email}</span>
            <button onClick={logout} className="p-2 rounded-md text-[var(--text-muted)] hover:text-red-400 transition-colors" data-testid="client-logout">
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto pt-20 px-4 pb-12">
        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-[var(--bg-card)] border border-[var(--border-strong)] rounded-lg p-1 w-fit">
          {["content", "brand"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab ? "bg-[#FF4400] text-white" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              }`}
              data-testid={`client-tab-${tab}`}
            >
              {tab === "content" ? "Content" : "Brand Profile"}
            </button>
          ))}
        </div>

        {activeTab === "content" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-24 bg-[var(--bg-card)] rounded-lg animate-pulse" />
                ))}
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-16 text-[var(--text-muted)]">No content yet.</div>
            ) : (
              <div className="space-y-3">
                {history.map((item) => {
                  const sc = STATUS_CONFIG[item.status || "draft"];
                  return (
                    <div key={item.history_id} className="bg-[var(--bg-card)] border border-[var(--border-strong)] rounded-lg p-4" data-testid={`client-card-${item.history_id}`}>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className={`px-2 py-0.5 rounded-full text-xs border ${sc.color}`}>{sc.label}</span>
                            <span className="text-xs font-['JetBrains_Mono'] text-[var(--text-muted)]">
                              {new Date(item.created_at).toLocaleDateString()}
                            </span>
                            <span className="px-2 py-0.5 rounded text-xs bg-[var(--accent-subtle)] text-[#FF4400]">{item.content_type}</span>
                          </div>
                          <h3 className="text-[var(--text-primary)] font-medium truncate">{item.title || item.topic}</h3>
                          <p className="text-sm text-[var(--text-muted)] truncate mt-1">
                            {(item.generated_draft || "").replace(/#{1,6}\s/g, "").replace(/\n/g, " ").substring(0, 100)}...
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {/* Status change for clients */}
                          <select
                            value={item.status || "draft"}
                            onChange={(e) => handleStatusChange(item, e.target.value)}
                            className="text-xs bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-md px-2 py-1.5 text-[var(--text-secondary)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]"
                            data-testid={`client-status-select-${item.history_id}`}
                          >
                            {clientStatusOptions.map((s) => (
                              <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => setViewItem(item)}
                            className="p-2 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors"
                            data-testid={`client-view-${item.history_id}`}
                          >
                            <Eye size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {/* Pagination */}
                {pages > 1 && (
                  <div className="flex items-center justify-center gap-4 pt-4">
                    <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="p-2 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-30">
                      <CaretLeft size={18} />
                    </button>
                    <span className="text-sm text-[var(--text-secondary)] font-['JetBrains_Mono']">{page}/{pages}</span>
                    <button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page >= pages} className="p-2 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-30">
                      <CaretRight size={18} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}

        {activeTab === "brand" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {brandProfile ? (
              <div className="bg-[var(--bg-card)] border border-[var(--border-strong)] rounded-lg p-6 space-y-4">
                <h2 className="text-lg font-semibold text-[var(--text-primary)]">Brand Profile</h2>
                {[
                  { label: "Brand Voice", value: brandProfile.brand_voice },
                  { label: "Target Audience", value: brandProfile.target_audience },
                  { label: "Industry", value: brandProfile.industry },
                  { label: "Value Proposition", value: brandProfile.value_proposition },
                  { label: "Content Pillars", value: (brandProfile.content_pillars || []).join(", ") },
                ].map(({ label, value }) =>
                  value ? (
                    <div key={label}>
                      <p className="text-xs font-['JetBrains_Mono'] text-[var(--text-muted)] mb-1">{label}</p>
                      <p className="text-sm text-[var(--text-secondary)]">{value}</p>
                    </div>
                  ) : null
                )}
              </div>
            ) : (
              <p className="text-center py-16 text-[var(--text-muted)]">No brand profile configured yet.</p>
            )}
          </motion.div>
        )}

        {/* View modal */}
        {viewItem && (
          <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center pt-16 px-4 overflow-y-auto" onClick={() => setViewItem(null)}>
            <div className="bg-[var(--bg-card)] border border-[var(--border-strong)] rounded-xl max-w-3xl w-full p-6 my-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-[var(--text-primary)] truncate">{viewItem.title || viewItem.topic}</h2>
                <button onClick={() => setViewItem(null)} className="p-2 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)]">
                  <span className="text-sm">Close</span>
                </button>
              </div>
              {isMultiSectionDraft(viewItem.generated_draft, viewItem.content_type) ? (
                <MultiSectionDisplay
                  data={parseDraftToSections(viewItem.generated_draft || "")}
                  isSaved={true}
                  exportMeta={{ title: viewItem.title || viewItem.topic, content_type: viewItem.content_type }}
                />
              ) : (
                <div className="prose prose-sm max-w-none text-sm text-[var(--text-secondary)] leading-relaxed prose-headings:text-[var(--text-primary)] prose-strong:text-[var(--text-primary)]">
                  <ReactMarkdown>{viewItem.generated_draft}</ReactMarkdown>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
